import useNotifications from '@context/notifications/useNotificationContext';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { Button, Form, Input, Space } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createCategory, updateCategory } from '../../api';
import type { InventoryCategory } from '../../types';

type Props = {
  data?: InventoryCategory;
  onSubmitted?: (result: InventoryCategory) => void;
};

type InventoryCategoryFormValues = {
  name: string;
  description?: string;
};

export function InventoryCategoryForm({ data, onSubmitted }: Props) {
  const { t } = useTranslation();
  const { openNotification } = useNotifications();
  const { handleError, createValidationError } = useSmartErrorHandling();
  const [form] = Form.useForm<InventoryCategoryFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const initialValues = useMemo<Partial<InventoryCategoryFormValues>>(
    () => ({
      name: data?.name ?? '',
      description: data?.description ?? '',
    }),
    [data]
  );

  const onFinish = async (values: InventoryCategoryFormValues) => {
    try {
      setSubmitting(true);
      const name = values.name?.trim();
      if (!name) throw createValidationError(t('inventory-category-name-required'));

      const description = values.description?.trim();
      let result: InventoryCategory;

      if (data?.categoryId) {
        result = await updateCategory(data.categoryId, { name, description });
        openNotification('topRight', {
          title: t('inventory-categories-notification-title'),
          description: t('inventory-categories-notification-updated'),
          type: 'Success',
        });
      } else {
        result = await createCategory({ name, ...(description ? { description } : {}) });
        openNotification('topRight', {
          title: t('inventory-categories-notification-title'),
          description: t('inventory-categories-notification-created'),
          type: 'Success',
        });
      }

      onSubmitted?.(result);
    } catch (error: unknown) {
      handleError(error as Error, {
        customTitle: t('inventory-categories-error-title'),
        customMessage: t('inventory-categories-error-save'),
        customType: 'Warning',
        forceShow: false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form<InventoryCategoryFormValues> layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
      <Form.Item
        name="name"
        label={t('inventory-category-name-label')}
        rules={[{ required: true, message: t('inventory-category-name-required') }]}
      >
        <Input placeholder={t('inventory-categories-field-name-placeholder')} />
      </Form.Item>

      <Form.Item name="description" label={t('inventory-category-description-label')}>
        <Input.TextArea rows={4} placeholder={t('inventory-categories-field-description-placeholder')} />
      </Form.Item>

      <Space>
        <Button htmlType="submit" type="primary" loading={submitting}>
          {data?.categoryId ? t('inventory-categories-button-update') : t('inventory-categories-button-create')}
        </Button>
      </Space>
    </Form>
  );
}
